/**
 * @file publish-check-links.ts
 * @description Куда ведёт ссылка проверки публикации (phase19.16 Р1; SB59,
 * SB74, SB113, SB125): раздел магазина (реквизиты, правовые документы) или
 * раздел конструктора сайта (меню, оформление, шаблон товара, вид корзины).
 *
 * Р3г: счётчики и баннер cookie — раздел «Счётчики и cookie», подсказка о
 * политике ПДн (SB137) — редактор правовых документов.
 *
 * Один реестр на окно публикации (`PublishChecksList`) и постоянную плашку
 * «Сайт работает без: …» (`LiveChecksNotice`, SB125 п. 5): новая ссылка —
 * запись здесь, а не ветка в компоненте.
 */

import { CART_LOOK_SELECTION, type BuilderSelection } from "../site-builder/selection";
import type { PublishCheck, PublishCheckLink } from "./types";

/** Ссылка на раздел конструктора с сохранением открытой страницы. */
export type BuilderHref = (patch: Partial<BuilderSelection>) => string;

export interface CheckLinkMeta {
  to: (slug: string, builder: BuilderHref) => string;
  label: string;
}

/** Раздел «Счётчики и cookie» (Р3г). */
const COUNTERS_LINK: CheckLinkMeta = { to: (_slug, builder) => builder({ panel: "counters", sub: null }), label: "Счётчики и cookie" };

/** Куда ведёт ссылка проверки: раздел магазина или раздел конструктора сайта. */
const LINK_META: Readonly<Record<PublishCheckLink, CheckLinkMeta>> = {
  requisites: { to: (slug) => `/shop/${slug}/settings/legal-profiles`, label: "Заполнить реквизиты" },
  "legal-documents": { to: (slug) => `/shop/${slug}/settings/legal-documents`, label: "Правовые документы" },
  menu: { to: (_slug, builder) => builder({ panel: "menu", sub: "header" }), label: "Открыть меню" },
  brand: { to: (_slug, builder) => builder({ panel: "settings", sub: "brand" }), label: "Настройки сайта" },
  appearance: { to: (_slug, builder) => builder({ panel: "appearance", sub: "colors" }), label: "Оформление" },
  "product-template": {
    to: (_slug, builder) => builder({ pageId: null, screen: "product", panel: null, sel: { kind: "page" } }),
    label: "Открыть шаблон",
  },
  // SB120 этап 4: слабый контраст корзины (`site_cart_contrast_low`) — экран «Корзина» с «Видом корзины».
  cart: {
    to: (_slug, builder) => builder(CART_LOOK_SELECTION),
    label: "Вид корзины",
  },
  // Р3г: предупреждения счётчиков и баннера cookie (`link: "counters"`) — раздел «Счётчики и cookie».
  counters: COUNTERS_LINK,
};

/**
 * Ссылка точнее раздела — по коду проверки (SB113: колонок меню подвала
 * больше 3 — сразу во вкладку «Подвал» меню, а не в «Шапку»).
 */
const CODE_LINK_META: Readonly<Record<string, CheckLinkMeta>> = {
  site_view_footer_columns_over_limit: {
    to: (_slug, builder) => builder({ panel: "menu", sub: "footer" }),
    label: "Открыть меню подвала",
  },
  // Р3г (SB134): вышла новая редакция текстов баннера — к «Обновить» в группе «Тексты».
  site_counters_platform_text_outdated: {
    to: (_slug, builder) => builder({ panel: "counters", sub: null }),
    label: "Обновить тексты баннера",
  },
  // Р3г (SB137): «Добавьте в политику ПДн абзац о …» — точнее раздела: в редактор документов.
  site_counters_privacy_hint: {
    to: (slug) => `/shop/${slug}/settings/legal-documents`,
    label: "Открыть редактор документов",
  },
};

/** Ссылка проверки: сначала по коду, затем по разделу; нет — `null`. */
export function checkLinkMeta(check: PublishCheck): CheckLinkMeta | null {
  return CODE_LINK_META[check.code] ?? (check.link ? LINK_META[check.link] : null);
}
